import _ from 'lodash'
import { useSelector } from 'react-redux'
import styled, { css } from 'styled-components'
import { useParams } from 'react-router-dom'

import { Box } from '../../components/Box'
import { CollapseContent } from '../../components/Collapse'
import { Flex } from '../../components/Flex'
import { Helmet } from '../../components/Helmet'
import { InfoBox } from '../../components/InfoBox'
import { Title } from '../../components/Text'
import { Icon } from '../../components/Icons'
import { brkmax, themeColor, themeTitleFontSize } from '../../components/theme'
import { ArrowIcon } from '../../containers/CompanyDetail/Reports'
import { LoginOverlay } from '../../containers/LoginOverlay'
import { useDateFormat } from '../../hooks'
import { useViewportSize } from '../../hooks/useViewportSize'
import { useAccountingStatus } from '../../hooks/useAccountingStatus'
import { Company } from '../../redux/companyDetail'
import { getCountryCode, getDomesticOfferingCountries, getPublicUrl } from '../../redux/config'
import { RegistrationChannel } from '../../redux/user'
import { isUserLoggedIn, getRegistrationChannel } from '../../redux/user/selectors'
import { interpolate } from '../../utils/interpolate'
import { formatUrl } from '../../utils/routes'
import { DeliveryOption } from '../utils/config'
import { CompanyInfoBoxes } from '../../components/CompanyInfoBoxes/CompanyInfoBoxes'

import { CompanyDetailFields } from './Configuration/config'
import { CompanyDetailPageProps, interpolationData, Layout, useCompanyDetailData } from './common'

const MoreInfoWrapper = styled(Flex)`
  border-top: 1px solid ${themeColor('borderPrimary')};
  border-bottom: 1px solid ${themeColor('borderPrimary')};
  display: block;
`
const Alert = styled.div`
  display: flex;
  align-items: center;
`

const AlertIcon = styled(Icon)`
  font-size: 20px;
  margin-right: 8px;
`

const SectionTitle = styled(Title)`
  ${brkmax(
    0,
    css`
      font-size: ${themeTitleFontSize('medium')};
    `
  )}
`

function getSingleOffer(companyDetailUrl: string, deliveryOption: DeliveryOption) {
  return {
    '@type': 'Offer',
    url: companyDetailUrl,
    priceCurrency: deliveryOption.currency,
    price: deliveryOption.netValue,
    itemCondition: 'https://schema.org/UsedCondition',
    availability: 'https://schema.org/InStock'
  }
}

function getAggregateOffer(companyDetailUrl: string, deliveryOptions: DeliveryOption[]) {
  return {
    '@type': 'AggregateOffer',
    offerCount: _.size(deliveryOptions),
    lowPrice: _.minBy(deliveryOptions, dopt => dopt.netValue)?.netValue,
    highPrice: _.maxBy(deliveryOptions, dopt => dopt.netValue)?.netValue,
    priceCurrency: _.head(deliveryOptions)?.currency
  }
}

function useLdJson(company?: Company, metaTitle?: string, metaDescription?: string) {
  const publicUrl = useSelector(getPublicUrl)
  const companyDetailUrl = formatUrl(publicUrl, location.pathname)

  if (!company) return ''

  const allDeliveryOptions = _.flatMap(company.reports, r => r.deliveryOptions)

  return JSON.stringify(
    {
      '@context': 'https://schema.org/',
      '@type': 'Product',
      name: metaTitle,
      description: metaDescription,
      sku: company?.companyIdentifier,
      brand: {
        '@type': 'Thing',
        name: 'CRIF business information'
      },
      offers:
        _.size(allDeliveryOptions) > 0
          ? _.size(allDeliveryOptions) === 1
            ? getSingleOffer(companyDetailUrl, allDeliveryOptions[0])
            : getAggregateOffer(companyDetailUrl, allDeliveryOptions)
          : undefined
    },
    null,
    2
  )
}

export const CompanyDetail = (props: CompanyDetailPageProps) => {
  const viewPort = useViewportSize()
  const {
    companyIdentifier,
    activeTab,
    companyDetail,
    fromSearch,
    loading,
    setActiveTab,
    generateFields,
    tabs,
    tc,
    t,
    umbracoPageConfig: upc,
    displayCompanyDescription,
    formatAddress,
    preferredProductsData,
    refreshPreferredProducts,
    loadingPreferredProducts
  } = useCompanyDetailData(props)

  const countryCode = useSelector(getCountryCode)
  const { companyIdentifier: companyIdentifierParam } = useParams()

  const domesticOfferingCountries = useSelector(getDomesticOfferingCountries)
  const loggedIn = useSelector(isUserLoggedIn)
  const registrationChannel = useSelector(getRegistrationChannel)

  const isSapOnline = useAccountingStatus()
  const showToAnonymousUsers = !!isSapOnline.disclaimers?.companyDetail?.showToAnonymousUsers
  const showToSelfRegisteredUsers = !!isSapOnline.disclaimers?.companyDetail?.showToSelfRegisteredUsers
  const showToSubscribers = !isSapOnline.loading && isSapOnline.disclaimers?.companyDetail?.showToSubscribers

  const isAnonymousUser = !loggedIn
  const isSelfRegisteredUser = registrationChannel === RegistrationChannel.BRQ
  const isSubscriber = registrationChannel === RegistrationChannel.AXE

  const displaySapInfo =
    !isSapOnline.loading &&
    isSapOnline.isOffline &&
    isSapOnline.disclaimers?.visibility?.showInCompanyDetail &&
    ((showToAnonymousUsers && isAnonymousUser) ||
      (showToSelfRegisteredUsers && isSelfRegisteredUser) ||
      (showToSubscribers && isSubscriber))

  const company = companyDetail?.company

  const { authFields, notAuthFields }: CompanyDetailFields = company
    ? generateFields(company)
    : { authFields: [], notAuthFields: [] }

  const filteredAuthFields = _.filter(authFields, x => !_.isEmpty(x.value))

  const { formatDate } = useDateFormat()

  const metaTitle = !_.isEmpty(upc?.seo?.metaTitle)
    ? interpolate(upc?.seo?.metaTitle || '', interpolationData(company, formatAddress, formatDate))
    : undefined

  const metaDescription = !_.isEmpty(upc?.seo?.metaDescription)
    ? interpolate(upc?.seo?.metaDescription || '', interpolationData(company, formatAddress, formatDate))
        .match(/([^.!?]+[.!?]+)|([^.!?]+$)/g)
        ?.filter(v => !v.includes('unavailableData'))
        .join('')
    : undefined

  const ldJson = useLdJson(company, metaTitle, metaDescription)

  return (
    <Layout
      companyIdentifier={companyIdentifier}
      loading={loading}
      companyDetail={companyDetail}
      tabs={tabs}
      setActiveTab={setActiveTab}
      fromSearch={!!fromSearch}
      activeTab={activeTab}
      umbracoPageConfig={upc}
      companyDescription={displayCompanyDescription ? metaDescription : undefined}
      preferredProductsData={preferredProductsData}
      refreshPreferredProductsData={refreshPreferredProducts}
      loadingPreferredProducts={loadingPreferredProducts}
    >
      <Helmet
        seo={{ metaTitle, metaDescription, keywords: upc?.seo?.keywords }}
        hreflang
        noIndex={
          !_.includes(
            _.map(_.concat(countryCode, domesticOfferingCountries), code => _.toLower(code)),
            _.toLower(companyIdentifierParam?.substring(0, 2))
          )
        }
      >
        {upc?.seo && company && <script type="application/ld+json">{ldJson}</script>}
      </Helmet>
      {viewPort?.isMobile || viewPort?.isTablet
        ? (!_.isEmpty(notAuthFields) || !_.isEmpty(filteredAuthFields)) && (
            <Flex flexWrap="wrap">
              <Box width={1}>
                <CollapseContent
                  renderHeader={open => (
                    <SectionTitle level="large">
                      {tc('Overview')}
                      <ArrowIcon type="arrowRightBlue" opened={open} width="40px" height="40px" />
                    </SectionTitle>
                  )}
                  defaultOpen
                >
                  <Flex flexWrap="wrap" marginX={[-1, -2]}>
                    <CompanyInfoBoxes
                      fields={notAuthFields}
                      defaultDesktopWidth={1 / 5}
                      pb={3}
                      automationId="companyOverviewItem"
                    />
                  </Flex>
                </CollapseContent>

                {!_.isEmpty(authFields) && (
                  <MoreInfoWrapper py={2} mt={2}>
                    <CollapseContent
                      renderHeader={open => (
                        <SectionTitle level="large">
                          {t('core:MoreInfo')}
                          <ArrowIcon type="arrow-right-blue" opened={open} width="40px" height="40px" />
                        </SectionTitle>
                      )}
                    >
                      <LoginOverlay visible={!loggedIn}>
                        <Flex flexWrap="wrap" marginX={[-1, -2]} px={loggedIn ? undefined : 2}>
                          {_.map(filteredAuthFields, ib => (
                            <InfoBox
                              boxProps={{ width: [1, 0.5, 1 / 5], pb: 3 }}
                              displayValues={loggedIn}
                              key={ib.label}
                              {...ib}
                              data-automation-id="companyOverviewItem"
                            />
                          ))}
                        </Flex>
                      </LoginOverlay>
                    </CollapseContent>
                  </MoreInfoWrapper>
                )}
              </Box>
            </Flex>
          )
        : (!_.isEmpty(notAuthFields) || !_.isEmpty(filteredAuthFields)) && (
            <Flex flexWrap="wrap">
              <Box width={1}>
                <Title level="large" padding={{ bottom: '16px' }}>
                  {tc('Overview')}
                </Title>

                <Flex flexWrap="wrap" marginX={[-1, -2]} mb={2}>
                  <CompanyInfoBoxes
                    fields={notAuthFields}
                    defaultDesktopWidth={1 / 5}
                    pb={3}
                    automationId="companyOverviewItem"
                  />
                </Flex>

                {!_.isEmpty(authFields) && (
                  <LoginOverlay visible={!loggedIn} minHeight="110px">
                    <Flex flexWrap="wrap" marginX={[-1, -2]} px={loggedIn ? undefined : 2}>
                      {_.map(filteredAuthFields, ib => (
                        <InfoBox
                          boxProps={{ width: [1, 0.5, 1 / 5], pb: 3 }}
                          displayValues={loggedIn}
                          key={ib.label}
                          {...ib}
                          data-automation-id="companyOverviewItem"
                        />
                      ))}
                    </Flex>
                  </LoginOverlay>
                )}
                {displaySapInfo ? (
                  <Alert>
                    <AlertIcon color="warning" type="ExclamationCircleOutlined" />
                    {tc('ServiceUnavailable')}
                  </Alert>
                ) : undefined}
              </Box>
            </Flex>
          )}
    </Layout>
  )
}
